import React, { useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Table, Checkbox, Tooltip, Skeleton } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AssetItemDto } from '@api/Api';
import { setFavoriteAssetAction } from '@redux/actions/assetsAction';
import Loader from '@components/Loader';
import UserAvatar from '@components/UserAvatar';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import AssetVersionsList from '@components/Assets/AssetVersionsList';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import Status from '@components/Approval/Status';
import { ActionToast } from '@components/Toasts';
import { useCombineAssets } from '@helpers/useCombineAssets';
import AssetActions from '@components/Tables/AssetActions';
import { ReorderEvent } from '@components/DraggableList/DraggableList';
import { CommentIcon, FavoriteIcon } from '@icons';
import { showReorderRequiresCustomSortHint } from '@helpers/showReorderRequiresCustomSortHint';

function AssetsListSkeleton() {
  return (
    <div className="assets_card_container" style={{ height: 56 }}>
      <div className="assets_card_container__preview">
        <Skeleton.Input style={{ width: '100%', height: 56 }} />
      </div>
    </div>
  );
}

function AssetsTable({
  assetsList,
  countToLoad = [],
  page,
  countToFetch,
  canShare = false,
  listLoading = false,
  onReorder,
  canReorganize
}: {
  assetsList: {
    node: AssetItemDto;
    cursor: string;
  }[];
  countToLoad?: string[];
  page: string;
  countToFetch: number;
  canShare?: boolean;
  listLoading?: boolean;
  onReorder?: (e: ReorderEvent) => void;
  canReorganize?: boolean;
}) {
  const combineAssets = useCombineAssets(assetsList);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth(true, 'internal');
  const dragIndexRef = useRef<number | null>(null);
  const draggingIdRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overState, setOverState] = useState<{
    id: string;
    position: 'top' | 'center' | 'bottom';
  } | null>(null);

  const getDropPosition = (y: number, height: number) => {
    const topBoundary = height * 0.33;
    const bottomBoundary = height * 0.67;
    if (y < topBoundary) return 'top' as const;
    if (y > bottomBoundary) return 'bottom' as const;
    return 'center' as const;
  };

  const getDragPayload = (e: React.DragEvent) => {
    const transferTypes = Array.from(e.dataTransfer.types);
    let type: string | undefined;
    if (transferTypes.includes('streamwork://type/asset')) {
      type = 'asset';
    } else if (transferTypes.includes('streamwork://type/folder')) {
      type = 'folder';
    } else if (transferTypes.includes('streamwork://type')) {
      type = e.dataTransfer.getData('streamwork://type') || undefined;
    } else if (draggingIdRef.current) {
      type = 'asset';
    }

    const idByToken = transferTypes
      .find((x) => x.startsWith('streamwork://id/'))
      ?.split('/')
      .slice(-1)[0];
    const id =
      idByToken ||
      (transferTypes.includes('streamwork://id')
        ? e.dataTransfer.getData('streamwork://id')
        : undefined) ||
      draggingIdRef.current ||
      '';

    return { type, id };
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        const preparePreview = assetPreviewThumbnails({
          url: node.asset?.versions[0].metadata.preview?.url || '',
          status: node.asset?.versions[0].metadata.preview?.status || '',
          type: node.asset?.versions[0].metadata.type || '',
          size: 40,
          format: 'img'
        });
        return (
          <div
            className="assets_user_container asset_card_wr"
            data-id={node.id}
          >
            <div id={`asset_${node.id}`}>
              <Checkbox
                value={node.id}
                className="checkbox_selected"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="asset">
                {preparePreview === 'pending' && (
                  <Loader
                    classNameWrapper="table-assets-loader-wrapper"
                    spinSize={40}
                    isSpinning={true}
                  />
                )}
                {preparePreview !== 'pending' &&
                  typeof preparePreview === 'string' && (
                    <img
                      draggable={false}
                      style={
                        node.asset?.versions[0].metadata.preview?.url
                          ? {}
                          : { width: 150, height: 100 }
                      }
                      src={preparePreview || ''}
                      alt=""
                    />
                  )}
              </div>

              <div className="assets_user_title">
                <span className="title">
                  <Tooltip
                    title={`${node.asset?.versions[0]?.name}.${node.asset?.versions[0]?.extension}`}
                    overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                    placement="topLeft"
                  >
                    <span className="copy">
                      {node.asset?.versions[0]?.name}.
                      {node.asset?.versions[0]?.extension}
                    </span>
                  </Tooltip>

                  <AssetVersionsList
                    versionsCount={node.asset?.versionsCount}
                    permissions={node.asset?.permissions}
                    versions={node.asset?.versions}
                    assetId={node.id}
                  />
                  {(node.asset?.versions[0].commentsCount || 0) > 0 &&
                    !node.deletedAt && (
                      <div className="info__comments">
                        <CommentIcon />
                        <span>{node.asset?.versions[0].commentsCount}</span>
                      </div>
                    )}
                </span>
                {!!node.asset?.approvalWorkflow && !node.deletedAt && (
                  <div className="assets_user_title_text_container">
                    <Status
                      showOverdueTag
                      status={node.asset.approvalWorkflow.status}
                      isPaused={node.asset.approvalWorkflow.isPaused}
                      deadline={node.asset.approvalWorkflow.deadline}
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <Tooltip
                placement="bottomRight"
                overlayClassName="toggle_favorite_overlay"
                title={
                  !node.isFavorite ? 'Add to favorite' : 'Remove from favorite'
                }
              >
                <div
                  className={classNames(
                    node.deletedAt && 'favorite_icon_deleted',
                    'favorite_icon'
                  )}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (node.deletedAt) return;
                    if (!node?.isFavorite) {
                      dispatch(
                        setFavoriteAssetAction(
                          {
                            assetId: node.id,
                            isFavorite: !node.isFavorite
                          },
                          page === 'favorite'
                        )
                      );
                      MixpanelService.track(
                        user.id,
                        MixpanelEventType.MANAGE_ASSETS,
                        {
                          addToFavorite: true,
                          deleteAsset: false,
                          newAsset: false,
                          externalLinkShare: false,
                          approvalRouting: false,
                          newCommentAsset: false,
                          annotationLeft: false,
                          taskFromComment: false
                        }
                      );
                      return;
                    }
                    dispatch(
                      setFavoriteAssetAction(
                        {
                          assetId: node.id,
                          isFavorite: !node.isFavorite
                        },
                        page === 'favorite'
                      )
                    );
                    toast(
                      <ActionToast
                        title="Media has been removed from favorites"
                        onUndo={undefined}
                        description={undefined}
                        closeToast={undefined}
                      />,
                      {
                        hideProgressBar: true,
                        bodyClassName: 'toast_container--invitation'
                      }
                    );
                  }}
                >
                  <FavoriteIcon
                    className={classNames(
                      node.isFavorite ? 'favorite' : 'non_favorite'
                    )}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 190,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        return (
          <div className="user_record">
            <Tooltip
              title={node.owner?.user?.name}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <div className="user_record__avatar">
                <UserAvatar
                  isActive={true}
                  size="large"
                  src={node.owner?.user?.picture.url || ''}
                  userEmail={node.owner?.user?.email || ''}
                  userName={node.owner?.user?.name || ''}
                />
              </div>
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 190,
      render: (text: string, { node }: { node: AssetItemDto }) => (
        <span className="time-state">
          {moment(node.createdAt).format('MMM DD, YYYY')}
        </span>
      )
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (text: string, { node }: { node: AssetItemDto }) => (
        <span className="time-state">
          {moment(node.modifiedAt).format('MMM DD, YYYY')}
        </span>
      )
    },
    {
      title: '',
      key: 'action',
      render: (text: string, { node }: { node: AssetItemDto }) => (
        <AssetActions asset={node} canShare={canShare} page={page} />
      )
    }
  ];
  return (
    <div
      className={classNames('table_container', {
        'table_container--dragging': !!draggingId
      })}
    >
      {!listLoading && (countToLoad?.length || 0) > 0 ? (
        <Table
          pagination={false}
          columns={columns}
          locale={{
            emptyText: countToLoad?.map((it, idx) => (
              <div key={idx} className="skeleton">
                <AssetsListSkeleton /> <br />
              </div>
            ))
          }}
          dataSource={assetsList}
          rowKey={({ node }: { node: AssetItemDto }) => node.id}
          rowClassName={({ node }: { node: AssetItemDto }) =>
            classNames({
              'table-row--dragging': draggingId === node.id,
              'table-row--over-top':
                overState?.id === node.id && overState?.position === 'top',
              'table-row--over-center':
                overState?.id === node.id && overState?.position === 'center',
              'table-row--over-bottom':
                overState?.id === node.id && overState?.position === 'bottom'
            })
          }
          onRow={({ node }, rowIndex) => ({
            draggable: true,
            onClick: () => {
              if (!node.deletedAt)
                navigate(
                  `/media-viewer/${node.id}/${
                    node.asset?.versions?.[0]?.id || ''
                  }`,
                  {
                    state: {
                      returnTo: location.pathname + location.search
                    }
                  }
                );
            },
            onDragOver: (e) => {
              const { type, id } = getDragPayload(e);
              if (type === 'asset' && id !== node.id) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                const y = e.clientY - rect.top;
                const position = getDropPosition(y, rect.height);
                setOverState({ id: node.id, position });
                const preview = document.querySelector(
                  '.drag-image'
                ) as HTMLElement | null;
                if (preview) {
                  preview.style.transform =
                    position === 'center' ? 'scale(0)' : 'scale(1)';
                }
              }
            },
            onDragLeave: () => {
              const preview = document.querySelector(
                '.drag-image'
              ) as HTMLElement | null;
              if (preview) {
                preview.style.transform = 'scale(1)';
              }
              setOverState(null);
            },
            onDrop: (e) => {
              e.preventDefault();
              const { type, id } = getDragPayload(e);
              const sourceIndex = dragIndexRef.current;
              setOverState(null);
              const preview = document.querySelector(
                '.drag-image'
              ) as HTMLElement | null;
              if (preview) {
                preview.remove();
              }
              setDraggingId(null);
              draggingIdRef.current = null;
              if (type !== 'asset' || !id) return;
              if (onReorder && sourceIndex !== null && rowIndex !== undefined) {
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                const y = e.clientY - rect.top;
                const position = getDropPosition(y, rect.height);
                if (position !== 'center') {
                  if (!canReorganize) {
                    showReorderRequiresCustomSortHint();
                    dragIndexRef.current = null;
                    return;
                  }
                  let targetIndex = rowIndex;
                  if (position === 'top') {
                    targetIndex =
                      rowIndex > sourceIndex ? rowIndex - 1 : rowIndex;
                  } else {
                    targetIndex =
                      rowIndex > sourceIndex ? rowIndex : rowIndex + 1;
                  }

                  if (targetIndex !== sourceIndex) {
                    const boundedTargetIndex = Math.max(
                      0,
                      Math.min(targetIndex, assetsList.length - 1)
                    );
                    onReorder({
                      item: { id, type: 'asset' },
                      prevIndex: sourceIndex,
                      newIndex: boundedTargetIndex
                    });
                  }
                  dragIndexRef.current = null;
                  return;
                }
              }
              combineAssets(id, node.id);
              dragIndexRef.current = null;
            },
            onDrag: (e) => {
              const preview = document.querySelector(
                '.drag-image'
              ) as HTMLElement | null;
              if (!preview) return;
              if (e.pageY || e.pageX) {
                preview.style.top = `${e.pageY + 10}px`;
                preview.style.left = `${e.pageX + 10}px`;
              }
            },
            onDragEnd: () => {
              const preview = document.querySelector('.drag-image');
              if (preview) {
                preview.remove();
              }
              setDraggingId(null);
              setOverState(null);
              dragIndexRef.current = null;
              draggingIdRef.current = null;
            },
            onDragStart: (e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('streamwork://id', node.id);
              e.dataTransfer.setData(`streamwork://id/${node.id}`, node.id);
              e.dataTransfer.setData('streamwork://type', 'asset');
              e.dataTransfer.setData('streamwork://type/asset', 'asset');
              dragIndexRef.current = rowIndex ?? null;
              draggingIdRef.current = node.id;
              setDraggingId(node.id);
              const ctx = document
                ?.querySelector(`#asset_${node?.id || ''}`)
                ?.getElementsByTagName('img')[0];

              if (!ctx) return;

              const dragImageContainer = document.createElement('div');
              const dragImage = ctx?.cloneNode(true) as any;
              const dragTitle = document.createElement('div');
              dragTitle.textContent = `${node.asset?.versions[0].name}.${node.asset?.versions[0].extension}`;
              dragImageContainer.classList.add('drag-image');
              dragImageContainer.classList.add('dragged_asset_container');
              document.body.appendChild(dragImageContainer);
              dragImageContainer.appendChild(dragImage);
              dragImageContainer.appendChild(dragTitle);
              e.dataTransfer.setDragImage(dragImageContainer, 0, 0);
              setTimeout(() => {
                dragImageContainer.style.transform = 'scale(1)';
              }, 1);
            }
          })}
        />
      ) : (
        countToLoad?.map((el, idx) => (
          <div key={idx} className="skeleton">
            <Skeleton.Input
              style={{ width: '100%', height: 56, marginBottom: 27 }}
              key={idx}
            />{' '}
            <br />{' '}
          </div>
        ))
      )}
      {new Array(countToFetch).fill('').map((el, idx) => (
        <div key={idx} className="skeleton">
          <Skeleton.Input
            style={{ width: '100%', height: 56, marginBottom: 27 }}
          />{' '}
          <br />{' '}
        </div>
      ))}
    </div>
  );
}

export default AssetsTable;
